/**
 * Pure helper — infers a JSON shape from a list of column sample values
 * and returns paths in the same shape as a MetaInfo `JsonStructureCache`
 * entry so the same UI (JsonShapePreview) can render either source.
 * Strings are JSON.parse'd; non-parseable strings and primitives skip.
 */
import { walkJsonPaths } from './jsonPathWalker';
import type {
  JsonStructureCachePath,
  JsonStructureCachePathType,
} from '../../../../types/electron';

export type InferJsonStructureResult = {
  paths: JsonStructureCachePath[];
  /** Rows considered (every entry in the input, incl. non-JSON). */
  sampledRows: number;
  /** Rows that walked successfully (resolved to an object or array). */
  jsonRows: number;
};

export function inferJsonStructureFromValues(
  values: ReadonlyArray<unknown>,
): InferJsonStructureResult {
  const sampledRows = values.length;
  const pathSeen = new Map<string, number>();
  const pathTypes = new Map<string, Set<JsonStructureCachePathType>>();
  let jsonRows = 0;

  for (const value of values) {
    let walkable: unknown = undefined;
    if (value !== null && typeof value === 'object') {
      walkable = value;
    } else if (typeof value === 'string' && value.trim()) {
      try {
        const parsed: unknown = JSON.parse(value);
        if (parsed !== null && typeof parsed === 'object') walkable = parsed;
      } catch {
        // not JSON — silently skip
      }
    }
    if (walkable === undefined) continue;
    jsonRows += 1;

    const seenInThisRow = new Set<string>();
    walkJsonPaths(walkable, (path, type) => {
      let typeSet = pathTypes.get(path);
      if (!typeSet) {
        typeSet = new Set();
        pathTypes.set(path, typeSet);
      }
      typeSet.add(type);
      if (seenInThisRow.has(path)) return;
      seenInThisRow.add(path);
      pathSeen.set(path, (pathSeen.get(path) ?? 0) + 1);
    });
  }

  if (jsonRows === 0) return { paths: [], sampledRows, jsonRows: 0 };

  const paths: JsonStructureCachePath[] = [];
  for (const [path, count] of pathSeen.entries()) {
    const typeSet = pathTypes.get(path) ?? new Set<JsonStructureCachePathType>();
    const type: JsonStructureCachePathType =
      typeSet.size > 1 ? 'mixed' : (typeSet.values().next().value ?? 'mixed');
    paths.push({ path, type, prevalence: count / jsonRows });
  }
  paths.sort(
    (a, b) => b.prevalence - a.prevalence || a.path.localeCompare(b.path),
  );
  return { paths, sampledRows, jsonRows };
}
